import {
  GuestJourneyBenchmarkSection,
  GuestJourneyBenchmarksKpiCardDTO,
  GuestJourneyBenchmarkTimeframe,
} from '../guest-journey-reports.types';

/**
 * BE `GET v2/guest-journey/benchmarks/timeframes` — the selectable reporting
 * timeframes. `{id, label}` pairs used verbatim as the Benchmarks timeframe
 * filter options; the first entry is the default selection. "First Half" only
 * appears once the current date is past July 1.
 */
export const benchmarkTimeframesFixture: GuestJourneyBenchmarkTimeframe[] = [
  { id: 'ytd', label: '2026' },
  { id: '1h2026', label: '1H 2026' },
  { id: 'lastyr', label: '2025' },
];

/**
 * BE `GET v2/guest-journey/benchmarks/overview` (spec v0.5) — the ordered array
 * of pre-formatted KPI cards (Frequency Median, Frequency Quartile, Churn
 * Threshold). Wire shape (`GuestJourneyBenchmarksKpiCardDTO[]`), so the mock
 * exercises the `toBenchmarksKpiCards` transform. `baselineLabel`/`baselineValue`
 * are the (nullable) baseline row; `delta` the (nullable) pill; Churn Threshold
 * is point-in-time (baseline + delta null).
 */
export const benchmarksOverviewFixture: GuestJourneyBenchmarksKpiCardDTO[] = [
  {
    eyebrow: 'FREQUENCY MEDIAN',
    value: '18 days',
    baselineLabel: 'Fast Casual — Bowls benchmark:',
    baselineValue: '17 days',
    delta: { text: '+1 day', direction: 'UP', invertColor: true },
    description: 'Half of returning customers come back within this window.',
  },
  {
    eyebrow: 'FREQUENCY QUARTILE',
    value: '2nd Quartile (25–50%)',
    baselineLabel: 'in Fast Casual — Bowls cuisine',
    baselineValue: null,
    delta: { text: 'Below Benchmark', direction: 'DOWN' },
    description:
      'Top quartile means a faster return cycle than peers in this cuisine.',
  },
  {
    eyebrow: 'CHURN THRESHOLD',
    value: '62 days',
    baselineLabel: null,
    baselineValue: null,
    delta: null,
    description:
      'This is the cutoff after which a customer is considered “Churned,” based on data from brands within the same cuisine.',
  },
];

/**
 * BE `GET v2/guest-journey/benchmarks/metrics` (spec v0.5) — the four-section
 * metrics grid. `brandValue`/`benchmarkValue` are BE-formatted display strings
 * (rendered verbatim); `metricName`/`metricDescription`/`section` are BE-resolved
 * labels; `vsDelta` is a movement payload vs. the cuisine baseline (`invertColor`
 * flips the color for higher-is-bad metrics). `performancePct`/
 * `benchmarkMarkerPct` are 0–100 PerfBar positions.
 */
export const benchmarksMetricsFixture: GuestJourneyBenchmarkSection[] = [
  {
    section: 'Engagement',
    rows: [
      {
        metricName: 'Signup to First Order Conversion',
        metricDescription:
          'Share of new members who place a first order within 30 days.',
        brandValue: '62%',
        benchmarkValue: '55%',
        vsDelta: { text: 'Above Benchmark', direction: 'UP' },
        performancePct: 72,
        benchmarkMarkerPct: 60,
      },
      {
        metricName: 'Signup to Third Order Conversion',
        metricDescription:
          'Share of new members who reach a third order within 90 days.',
        brandValue: '28%',
        benchmarkValue: '34%',
        vsDelta: { text: 'Below Benchmark', direction: 'DOWN' },
        performancePct: 41,
        benchmarkMarkerPct: 55,
      },
    ],
  },
  {
    section: 'Loyalty',
    rows: [
      {
        metricName: 'New Members per Location per Month',
        metricDescription:
          'Average new loyalty members acquired per location each month.',
        brandValue: '142',
        benchmarkValue: '120',
        vsDelta: { text: 'Above Benchmark', direction: 'UP' },
        performancePct: 68,
        benchmarkMarkerPct: 55,
      },
      {
        metricName: 'Contribution Rate',
        metricDescription: 'Share of net sales attributed to loyalty members.',
        brandValue: '31%',
        benchmarkValue: '38%',
        vsDelta: { text: 'Below Benchmark', direction: 'DOWN' },
        performancePct: 44,
        benchmarkMarkerPct: 58,
      },
      {
        metricName: 'Activity Rate',
        metricDescription:
          'Share of members who ordered in the trailing 90 days.',
        brandValue: '47%',
        benchmarkValue: '45%',
        vsDelta: { text: 'Above Benchmark', direction: 'UP' },
        performancePct: 58,
        benchmarkMarkerPct: 52,
      },
    ],
  },
  {
    section: 'Channel Mix',
    rows: [
      {
        metricName: 'Mobile Order Net Sales %',
        metricDescription: 'Share of net sales from mobile-app orders.',
        brandValue: '54%',
        benchmarkValue: '48%',
        vsDelta: { text: 'Above Benchmark', direction: 'UP' },
        performancePct: 60,
        benchmarkMarkerPct: 50,
      },
      {
        metricName: 'Web Order Net Sales %',
        metricDescription: 'Share of net sales from web orders.',
        brandValue: '21%',
        benchmarkValue: '27%',
        vsDelta: { text: 'Below Benchmark', direction: 'DOWN' },
        performancePct: 42,
        benchmarkMarkerPct: 55,
      },
    ],
  },
  {
    section: 'Spend',
    rows: [
      {
        metricName: 'Average Check',
        metricDescription: 'Average net sales per order.',
        brandValue: '$24.80',
        benchmarkValue: '$22.50',
        vsDelta: { text: 'Above Benchmark', direction: 'UP' },
        performancePct: 64,
        benchmarkMarkerPct: 55,
      },
      {
        metricName: 'Spend (First 30 Days)',
        metricDescription:
          'Average net sales per member in their first 30 days.',
        brandValue: '$112.40',
        benchmarkValue: '$98.00',
        vsDelta: { text: 'Above Benchmark', direction: 'UP' },
        performancePct: 66,
        benchmarkMarkerPct: 56,
      },
      {
        metricName: 'Spend (First 90 Days)',
        metricDescription:
          'Average net sales per member in their first 90 days.',
        brandValue: '$284.50',
        benchmarkValue: '$250.00',
        vsDelta: { text: 'Above Benchmark', direction: 'UP' },
        performancePct: 63,
        benchmarkMarkerPct: 54,
      },
      {
        metricName: 'Spend (First 365 Days)',
        metricDescription:
          'Average net sales per member in their first 365 days.',
        brandValue: '$1,148.20',
        benchmarkValue: '$1,020.00',
        vsDelta: { text: 'Above Benchmark', direction: 'UP' },
        performancePct: 61,
        benchmarkMarkerPct: 54,
      },
    ],
  },
];
